import React from "react";
import BannerImg from "../../assets/images/banner/project-management.png";
import EnterpriseServicePage from "../../components/services/enterprise-service-page";

const schemas = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Dedicated Engineering Teams",
    serviceType: "Embedded Engineering Team",
    description:
      "Dedicated engineering teams for enterprises that need embedded software delivery, modernization support and long-term technical ownership.",
    provider: {
      "@type": "Organization",
      name: "Avani Cimcon Technologies",
      url: "https://www.avanicimcon.com/",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.avanicimcon.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.avanicimcon.com/it-consulting-services" },
      {
        "@type": "ListItem",
        position: 3,
        name: "Dedicated Engineering Teams",
        item: "https://www.avanicimcon.com/services/dedicated-engineering-teams",
      },
    ],
  },
];

const DedicatedEngineeringTeams = () => (
  <EnterpriseServicePage
    title="Dedicated Engineering Teams for Enterprise Delivery, Modernization & AI | Avani Cimcon"
    description="Dedicated engineering teams from Avani Cimcon help enterprises extend capacity, maintain continuity and deliver modernization work with embedded accountability."
    keywords="dedicated engineering teams, embedded engineering teams, enterprise software engineering, offshore development, managed engineering teams, legacy modernization, AI enablement, delivery governance"
    canonical="https://www.avanicimcon.com/services/dedicated-engineering-teams"
    ogImage="https://www.avanicimcon.com/images/banner/project-management.png"
    hero={{
      eyebrow: "Dedicated Engineering Teams",
      heading: "Dedicated engineering teams that behave like an extension of your own organization",
      lead:
        "When the work is too important to hand to a disconnected vendor model, an embedded team gives you continuity, technical depth and stronger day-to-day collaboration.",
      tags: ["Embedded delivery", "Enterprise accountability", "Modernization support", "AI readiness"],
      primaryCta: { label: "Talk to Us", href: "/contact-us" },
      secondaryCta: { label: "See Managed Services", href: "/services/managed-services" },
      image: BannerImg,
      panelKicker: "Team capacity with governance.",
      panelTitle: "Add delivery horsepower without losing control.",
      panelText:
        "Dedicated teams help enterprises move faster on modernization, product work and integration while keeping knowledge close to the business.",
      stats: [
        { value: "Embedded", label: "Long-term team continuity" },
        { value: "Cross-functional", label: "Architecture, QA and delivery" },
        { value: "Global", label: "International delivery experience" },
      ],
    }}
    whatItIs={{
      eyebrow: "What It Is",
      title: "A dedicated team model built for complex enterprise work",
      lead: "An embedded delivery model for software that needs sustained ownership.",
      paragraphs: [
        "Dedicated engineering teams are useful when the work needs more than project-by-project execution. They help organizations keep product knowledge, technical context and delivery accountability in one place over time.",
        "This model fits the company's broader enterprise software heritage because it supports long-running systems, modernization programs and vertical products that must evolve without losing stability.",
      ],
    }}
    whoItIsFor={{
      title: "Who it is for",
      lead: "Teams that need steady capability rather than one-time project staffing.",
      items: [
        "Enterprises with a roadmap that spans several releases or product cycles",
        "Organizations that want embedded architects, developers and QA support",
        "Businesses modernizing platforms while keeping the existing stack alive",
      ],
    }}
    businessProblems={{
      eyebrow: "Problems Solved",
      title: "The delivery problems dedicated teams help solve",
      lead: "Capacity, context and continuity are the usual blockers.",
      items: [
        {
          title: "Insufficient internal bandwidth",
          text: "The enterprise team is stretched across support, enhancements and new delivery.",
        },
        {
          title: "Knowledge fragmentation",
          text: "The work depends on systems that have lived through several technology eras.",
        },
        {
          title: "Slow execution",
          text: "The business needs faster delivery without sacrificing quality or control.",
        },
      ],
    }}
    scenarios={{
      eyebrow: "Typical Scenarios",
      title: "Common situations where dedicated teams make sense",
      lead: "Situations where a permanent-feeling team is more valuable than a revolving bench.",
      items: [
        { title: "Product evolution", text: "The product roadmap needs a stable engineering group that understands the codebase." },
        { title: "Modernization programs", text: "The organization needs a team that can preserve, stabilize and modernize together." },
        { title: "Integration work", text: "Data exchange and enterprise interoperability need people who know the platform context." },
        { title: "AI enablement", text: "AI needs to be layered into systems by a team that understands the current architecture." },
      ],
    }}
    deliveryApproach={{
      eyebrow: "How We Deliver",
      title: "How Avani Cimcon structures embedded delivery",
      lead: "A team model that keeps ownership close and accountability visible.",
      paragraphs: [
        "We align team composition to the actual problem: architecture, application support, QA, implementation, integration or product engineering. The goal is to create a delivery unit that can work with enterprise constraints instead of around them.",
        "That also makes the model useful for international delivery. Teams can support distributed stakeholders while keeping communication, traceability and release governance in one place.",
      ],
      callout: {
        title: "Why the model works",
        items: [
          "Domain understanding stays with the team",
          "Changes move through one accountable delivery stream",
          "Long-term knowledge reduces rework and handoff loss",
          "AI and modernization can be introduced gradually",
        ],
      },
    }}
    industries={{
      eyebrow: "Industries Served",
      title: "Dedicated teams across the company’s strongest enterprise sectors",
      lead: "Build around proven vertical depth rather than generic staffing language.",
      items: [
        { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Reservation, booking and operations platforms." },
        { label: "Healthcare", href: "/healthcare", text: "Interoperability and patient workflow systems." },
        { label: "Insurance", href: "/insurance", text: "Regulated policy and claims environments." },
        { label: "Legal", href: "/legal", text: "Document-heavy matter and billing workflows." },
        { label: "DXchange", href: "/dxchange", text: "Integration and enterprise data exchange." },
        { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules-driven workflow orchestration." },
      ],
    }}
    framework={{
      eyebrow: "Modernization Framework",
      title: "Dedicated teams can carry the modernization framework end-to-end",
      lead: "The same team can move from discovery to execution without losing context.",
      items: [
        { title: "Assess", text: "Document the current state and the change backlog." },
        { title: "Preserve", text: "Keep the business-critical workflows intact." },
        { title: "Stabilize", text: "Remove friction and reduce delivery risk." },
        { title: "Integrate", text: "Connect older and newer systems cleanly." },
      ],
    }}
    aiOpportunities={{
      eyebrow: "Related AI Opportunities",
      title: "Where a dedicated team can support AI adoption",
      lead: "AI projects go better when the engineering team already knows the operating environment.",
      items: [
        {
          title: "Enterprise AI Enablement",
          text: "Use embedded teams to layer AI into real workflows and products.",
          href: "/services/enterprise-ai-enablement",
          linkLabel: "See the service page",
        },
        {
          title: "Enterprise AI Overview",
          text: "Read the enterprise AI hub for the broader governance story.",
          href: "/enterprise-ai",
          linkLabel: "Open the AI hub",
        },
        {
          title: "Legacy Modernization",
          text: "Use the same team model to preserve and modernize the application estate.",
          href: "/services/legacy-modernization",
          linkLabel: "Review the framework",
        },
      ],
    }}
    relatedProducts={{
      eyebrow: "Related Products",
      title: "Products that prove the team can build and maintain real systems",
      lead: "Dedicated teams are strongest when they can point to proven platform heritage.",
      items: [
        { label: "DXchange", href: "/dxchange", text: "Enterprise integration and API management." },
        { label: "CMS", href: "/cms", text: "Content operations and portal delivery." },
        { label: "CARMA", href: "/carma", text: "Product heritage in enterprise software." },
      ],
    }}
    relatedServices={{
      eyebrow: "Related Services",
      title: "Other services in the same enterprise delivery family",
      lead: "Pair embedded teams with managed support or modernization when the roadmap needs both.",
      items: [
        { label: "Managed Services", href: "/services/managed-services", text: "Keep existing systems stable and supported." },
        { label: "Legacy Modernization", href: "/services/legacy-modernization", text: "Preserve, stabilize and modernize safely." },
        { label: "Enterprise AI Enablement", href: "/services/enterprise-ai-enablement", text: "Introduce governed AI in a controlled way." },
      ],
    }}
    cta={{
      eyebrow: "Talk to Us",
      title: "Need an embedded engineering team that can stay with the work?",
      text: "Let's discuss the mix of architecture, delivery and support your roadmap actually needs.",
      primary: { label: "Talk to Us", href: "/contact-us" },
      secondary: { label: "Discuss Your Modernization Roadmap", href: "/services/legacy-modernization" },
    }}
    schemas={schemas}
  />
);

export default DedicatedEngineeringTeams;
